import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { UploadCloud, ArrowLeft, Video, Image as ImageIcon, Loader2 } from "lucide-react";
import { AFRICAN_COUNTRIES } from "@/lib/countries";
import BottomNav from "@/components/BottomNav";

const CATEGORIES = [
    { v: "fashion", l: "Mode & Vêtements" },
    { v: "beauty", l: "Beauté & Cosmétique" },
    { v: "food", l: "Nourriture & Restauration" },
    { v: "electronics", l: "Électronique" },
    { v: "home", l: "Maison & Déco" },
    { v: "services", l: "Services" },
    { v: "cars", l: "Automobile" },
    { v: "other", l: "Autre" },
];

export default function UploadPage() {
    const { user, loading } = useAuth();
    const navigate = useNavigate();
    const [form, setForm] = useState({ title: "", description: "", price: "", category: "other", city: "", country: "" });
    const [categories, setCategories] = useState(CATEGORIES);
    const [image, setImage] = useState(null);
    const [video, setVideo] = useState(null);
    const [imagePreview, setImagePreview] = useState(null);
    const [videoPreview, setVideoPreview] = useState(null);
    const [videoDuration, setVideoDuration] = useState(0);
    const [submitting, setSubmitting] = useState(false);
    const [progress, setProgress] = useState(0);
    const videoInputRef = useRef(null);

    useEffect(() => {
        if (!loading) {
            if (!user) navigate("/login");
            else if (user.role !== "seller") navigate("/onboarding");
            else {
                setForm((f) => ({
                    ...f,
                    city: f.city || user.city || "",
                    country: f.country || user.country || "",
                }));
            }
        }
    }, [user, loading, navigate]);

    useEffect(() => {
        api.get("/categories")
            .then((r) => {
                if (r.data?.length) setCategories(r.data.map((c) => ({ v: c.slug, l: c.label })));
            })
            .catch((err) => console.debug("categories fetch failed", err?.response?.status));
    }, []);

    const onVideoChange = (f) => {
        if (!f) return;
        if (f.size > 100 * 1024 * 1024) {
            toast.error("Vidéo trop lourde (max 100 Mo). Filmez en qualité moyenne ou raccourcissez.");
            return;
        }
        setVideo(f);
        const url = URL.createObjectURL(f);
        setVideoPreview(url);
        // check duration
        const v = document.createElement("video");
        v.preload = "metadata";
        v.onloadedmetadata = () => {
            setVideoDuration(v.duration);
            if (v.duration > 85) {
                toast.warning("Vidéo > 80s : elle sera coupée à 80s automatiquement");
            }
        };
        v.src = url;
    };

    const onImageChange = (f) => {
        if (!f) return;
        setImage(f);
        setImagePreview(URL.createObjectURL(f));
    };

    const submit = async (e) => {
        e.preventDefault();
        if (!form.title || !form.description || !form.price || !form.city || !form.country || !image || !video) {
            toast.error("Remplissez tous les champs (ville, pays inclus) et ajoutez image + vidéo");
            return;
        }
        setSubmitting(true);
        setProgress(0);
        const fd = new FormData();
        fd.append("title", form.title);
        fd.append("description", form.description);
        fd.append("price", form.price);
        fd.append("category", form.category);
        fd.append("city", form.city);
        fd.append("country", form.country);
        fd.append("image", image);
        fd.append("video", video);
        try {
            await api.post("/products", fd, {
                headers: { "Content-Type": "multipart/form-data" },
                onUploadProgress: (evt) => {
                    if (evt.total) setProgress(Math.round((evt.loaded * 100) / evt.total));
                },
                timeout: 300000,
            });
            toast.success("Produit publié !");
            navigate("/dashboard");
        } catch (e) {
            // Extract the most useful info from any error shape
            let msg = "Erreur d'envoi";
            const status = e?.response?.status;
            const detail = e?.response?.data?.detail;
            if (detail) {
                msg = detail;
            } else if (status === 413) {
                msg = "Vidéo trop lourde (limite serveur). Filmez à qualité inférieure.";
            } else if (status === 408) {
                msg = "Serveur trop lent à compresser. Vidéo plus courte ou moins HD.";
            } else if (status === 502 || status === 520 || status === 524) {
                msg = "Serveur temporairement indisponible (erreur " + status + "). Réessayez dans 30s.";
            } else if (e?.code === "ECONNABORTED" || e?.message?.includes("timeout")) {
                msg = "Envoi trop lent (timeout réseau). Vérifiez votre connexion.";
            } else if (status) {
                msg = `Erreur ${status} : ${e?.message || "envoi échoué"}`;
            } else if (e?.message) {
                msg = `Erreur réseau : ${e.message}`;
            }
            console.error("Upload failed", { status, detail, code: e?.code, message: e?.message });
            toast.error(msg, { duration: 8000 });
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="min-h-screen bg-[#050505] text-white pb-32 grain-bg">
            <div className="max-w-lg mx-auto px-4 pt-6">
                <button onClick={() => navigate(-1)} className="flex items-center gap-2 text-white/70 hover:text-white mb-6" data-testid="back-btn">
                    <ArrowLeft className="w-5 h-5" /> <span className="font-body text-sm">Retour</span>
                </button>

                <h1 className="font-display font-black text-3xl tracking-tight mb-2">Publier un produit</h1>
                <p className="text-white/60 font-body text-sm mb-6">Filmez votre article et remplissez les détails</p>

                <form onSubmit={submit} className="space-y-5">
                    {/* Video upload */}
                    <div>
                        <Label className="text-white/80 font-body">Vidéo courte (max 80s, 100 Mo, tout format) *</Label>
                        <div
                            onClick={() => videoInputRef.current?.click()}
                            className="mt-2 relative rounded-2xl border-2 border-dashed border-white/20 bg-white/5 aspect-[9/16] max-h-72 flex items-center justify-center cursor-pointer hover:border-[#FF5722] transition-colors overflow-hidden"
                            data-testid="video-drop-zone"
                        >
                            {videoPreview ? (
                                <video src={videoPreview} className="w-full h-full object-cover" muted playsInline autoPlay loop />
                            ) : (
                                <div className="text-center px-4">
                                    <Video className="w-10 h-10 text-white/40 mx-auto mb-2" />
                                    <p className="text-white/60 font-body text-sm">Cliquez pour choisir une vidéo</p>
                                    <p className="text-white/40 font-body text-xs mt-1">Sera compressée en H.264 640p</p>
                                </div>
                            )}
                        </div>
                        <input
                            ref={videoInputRef}
                            type="file"
                            accept="video/*,.mp4,.mov,.avi,.mkv,.webm,.3gp,.flv,.wmv,.m4v,.ogv,.mpeg,.mpg"
                            data-testid="video-input"
                            onChange={(e) => onVideoChange(e.target.files?.[0])}
                            className="hidden"
                        />
                        {videoDuration > 0 && (
                            <p className="text-xs text-white/50 mt-1 font-body">
                                Durée : {Math.round(videoDuration)}s
                                {video && ` · Taille : ${(video.size / (1024 * 1024)).toFixed(1)} Mo`}
                                {video && video.size > 60 * 1024 * 1024 && (
                                    <span className="text-yellow-400"> · Envoi long possible sur réseau lent</span>
                                )}
                            </p>
                        )}
                    </div>

                    {/* Image */}
                    <div>
                        <Label className="text-white/80 font-body">Photo du produit *</Label>
                        <label className="mt-2 relative block rounded-2xl border-2 border-dashed border-white/20 bg-white/5 h-32 flex items-center justify-center cursor-pointer hover:border-[#FF5722] transition-colors overflow-hidden">
                            {imagePreview ? (
                                <img src={imagePreview} alt="" className="w-full h-full object-cover" />
                            ) : (
                                <div className="flex items-center gap-2 text-white/60 font-body text-sm">
                                    <ImageIcon className="w-5 h-5" />
                                    Ajouter une photo
                                </div>
                            )}
                            <input
                                type="file"
                                accept="image/*"
                                data-testid="image-input"
                                onChange={(e) => onImageChange(e.target.files?.[0])}
                                className="hidden"
                            />
                        </label>
                    </div>

                    <div>
                        <Label htmlFor="title" className="text-white/80 font-body">Titre *</Label>
                        <Input
                            id="title"
                            data-testid="input-title"
                            value={form.title}
                            onChange={(e) => setForm({ ...form, title: e.target.value })}
                            placeholder="Ex: Robe wax haute couture"
                            className="mt-1 bg-white/5 border-white/10 h-12 text-white placeholder:text-white/30"
                        />
                    </div>

                    <div>
                        <Label htmlFor="description" className="text-white/80 font-body">Description *</Label>
                        <Textarea
                            id="description"
                            data-testid="input-description"
                            value={form.description}
                            onChange={(e) => setForm({ ...form, description: e.target.value })}
                            placeholder="Taille, matière, disponibilité, livraison..."
                            rows={3}
                            className="mt-1 bg-white/5 border-white/10 text-white placeholder:text-white/30"
                        />
                    </div>

                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <Label htmlFor="price" className="text-white/80 font-body">Prix (FCFA) *</Label>
                            <Input
                                id="price"
                                data-testid="input-price"
                                type="number"
                                value={form.price}
                                onChange={(e) => setForm({ ...form, price: e.target.value })}
                                placeholder="15000"
                                className="mt-1 bg-white/5 border-white/10 h-12 text-white placeholder:text-white/30"
                            />
                        </div>
                        <div>
                            <Label className="text-white/80 font-body">Catégorie</Label>
                            <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                                <SelectTrigger className="mt-1 bg-white/5 border-white/10 h-12 text-white" data-testid="select-category">
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    {categories.map((c) => (
                                        <SelectItem key={c.v} value={c.v}>{c.l}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                    </div>

                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <Label htmlFor="city" className="text-white/80 font-body">Ville *</Label>
                            <Input
                                id="city"
                                data-testid="input-product-city"
                                value={form.city}
                                onChange={(e) => setForm({ ...form, city: e.target.value })}
                                placeholder="Ex: Abidjan"
                                className="mt-1 bg-white/5 border-white/10 h-12 text-white placeholder:text-white/30"
                            />
                        </div>
                        <div>
                            <Label className="text-white/80 font-body">Pays *</Label>
                            <Select value={form.country} onValueChange={(v) => setForm({ ...form, country: v })}>
                                <SelectTrigger className="mt-1 bg-white/5 border-white/10 h-12 text-white" data-testid="select-product-country">
                                    <SelectValue placeholder="Choisir" />
                                </SelectTrigger>
                                <SelectContent className="max-h-64">
                                    {AFRICAN_COUNTRIES.map((c) => (
                                        <SelectItem key={c} value={c}>{c}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                    </div>

                    {submitting && (
                        <div className="glass rounded-2xl p-4 flex items-center gap-3">
                            <Loader2 className="w-5 h-5 animate-spin text-[#FF5722]" />
                            <div className="flex-1">
                                <p className="text-sm font-body text-white/90">
                                    {progress < 100 ? `Envoi ${progress}%` : "Compression FFmpeg en cours..."}
                                </p>
                                <div className="w-full h-1.5 bg-white/10 rounded-full mt-1 overflow-hidden">
                                    <div className="h-full bg-[#FF5722] transition-all" style={{ width: `${progress < 100 ? progress : 100}%` }} />
                                </div>
                            </div>
                        </div>
                    )}

                    <Button
                        type="submit"
                        disabled={submitting}
                        data-testid="submit-product"
                        className="w-full h-14 orange-btn text-white font-body font-bold text-base rounded-full flex items-center gap-2"
                    >
                        <UploadCloud className="w-5 h-5" />
                        {submitting ? "Publication..." : "Publier"}
                    </Button>
                </form>
            </div>
            <BottomNav />
        </div>
    );
}
